
import { floatVal, Nullable } from '@jamesgmarks/utilities';
import {
  ILiftSubscriptions,
  ISubscriptions,
  IVariableBasePriceOverrides,
} from '@llws/lift-entity-interfaces';
import {
  ICommissionSchemeData,
} from 'src/components/pages/Subscriptions/EditorComponents/CommissionEditor/CommissionEditor';
import {
  isVBPOChanged,
  IVBPData,
} from 'src/components/pages/Subscriptions/EditorComponents/VariableBasePriceEditor/VariableBasePriceEditor';
import { ISubscriptionData, TJsonNoteWithFlag } from 'src/components/pages/Subscriptions/form/types';

export interface SubscriptionCommandPayload {
  subscription: Partial<ISubscriptions>;
  commissionScheme?: Nullable<{
    add: Partial<ICommissionSchemeData>[];
    update: Partial<ICommissionSchemeData>[];
  }>;
  variableBasePriceOverrides?: {
    add: Partial<IVariableBasePriceOverrides>[];
    update: Partial<IVariableBasePriceOverrides>[];
    remove: number[];
  };
  buildings?: {
    add: { id: number; name?: string; price: string }[];
    remove: number[];
  };
  liftSubscriptions?: {
    add?: Partial<ILiftSubscriptions>;
    update?: Partial<ILiftSubscriptions>;
  }
}

export function buildSubscriptionCommandPayload(args: {
  subscriptionData: ISubscriptionData;
  commissionSchemeData: ICommissionSchemeData;
  vBPData: IVBPData;
  notesData?: TJsonNoteWithFlag[];
  storeSubscription: Nullable<ISubscriptions>;
}): SubscriptionCommandPayload {
  const {
    subscriptionData,
    commissionSchemeData,
    vBPData,
    notesData,
    storeSubscription,
  } = args;

  if (!subscriptionData.billingFrequencyId) {
    throw new Error('Billing frequency ID is required');
  }

  const subscription: Partial<ISubscriptions> = {
    id: storeSubscription?.id,
    startDate: subscriptionData.startDate,
    expiryDate: subscriptionData.expiryDate,
    baseSubscriptionId: subscriptionData.baseSubscriptionId!,
    clientId: subscriptionData.clientId!,
    discountRate: subscriptionData.discountRate / 100, // making sure to convert to decimal
    quoteId: subscriptionData.quoteId,
    revenueTrackingCode: subscriptionData.revenueTrackingCode,
    poNumber: subscriptionData.poNumber,
    invoiceDescription: subscriptionData.invoiceDescription,
    rateExpiryDate: subscriptionData.rateExpiryDate,
    minPropertiesForRate: subscriptionData.minimumPropertiesForRate,
    maxPropertiesForRate: subscriptionData.maxPropertiesForRate,
    salesRepId: subscriptionData.salesRepId,
    commissionRate: (subscriptionData?.commissionRate ?? 0) / 100, // making sure to convert to decimal
    notes: subscriptionData.notes,
    billingFrequencyId: subscriptionData.billingFrequencyId,
    evenBuildingSplit: subscriptionData.evenBuildingSplit,
    customConfiguration: subscriptionData.customConfiguration,
    basePrice: subscriptionData.basePrice,
    costPerLeadMax: subscriptionData.costPerLeadMax,
  };

  if (subscriptionData.basePriceScalar !== null && subscriptionData.basePriceScalar !== undefined) {
    subscription.basePriceScalar = subscriptionData.basePriceScalar;
  }

  const commissionScheme = commissionSchemeData.scheme
    ? {
      update: storeSubscription?.id ? [commissionSchemeData] : [],
      add: storeSubscription?.id ? [] : [commissionSchemeData],
    }
    : null;

  // 3. VBP overrides diffing
  const prevVBPO = storeSubscription?.variableBasePriceOverrides ?? [];
  const currVBPO = vBPData.rangedVBPO.map(({ range, ...rest }) => rest);

  const prevById = new Map(prevVBPO.map((v) => [v.id, v]));
  const currById = new Map(currVBPO.map((v) => [v.id, v]));

  const added = currVBPO.filter((v) => !v.id);
  const updated = currVBPO.filter((v) => v.id && isVBPOChanged(v, prevById.get(v.id)));

  const removed = prevVBPO.filter((v) => !currById.has(v.id)).map((v) => v.id!);

  const variableBasePriceOverrides = {
    add: added,
    update: updated,
    remove: removed,
  };

  // 4. Buildings diffing
  const prevBuildings = new Map(
    (storeSubscription?.subscriptionsHasBuildings ?? []).map((shb) => [shb.buildingId, shb.price]),
  );
  const currBuildingsMap = new Map(subscriptionData.buildingsData.map((b) => [b.id, b]));

  const createBuildingPayload = (building: { id: number; name: string; price: string }) => ({
    id: building.id,
    name: building.name,
    price: building.price?.toString() ?? '0',
  });

  const buildings = {
    add: subscriptionData.buildingsData
      .filter(b => !prevBuildings.has(b.id))
      .map(createBuildingPayload),

    update: subscriptionData.buildingsData
      .filter(b => {
        if (!prevBuildings.has(b.id)) return false;
        const prevPrice = floatVal(prevBuildings.get(b.id) ?? '0');
        const currPrice = floatVal(b.price ?? '0');
        return prevPrice !== currPrice;
      })
      .map(createBuildingPayload),

    remove: [...prevBuildings.keys()].filter(id => !currBuildingsMap.has(id)),
  };

  const notesToSave = (notesData ?? []).map(({ localOnly, ...note }) => note);

  subscription.notes = notesToSave.length > 0 ? notesToSave : [];

  const liftSubscriptionData = {
    subscriptionId: storeSubscription?.id ?? undefined, // using undefined instead of null because of typing issues sine this comes from @llws/lift-entity-interfaces
    minimumValue: subscriptionData.liftSubscriptions.minimumValue,
    costPerSingleFamilyHome: subscriptionData.liftSubscriptions.costPerSingleFamilyHome,
    costPerMultiFamilyHome: subscriptionData.liftSubscriptions.costPerMultiFamilyHome,
  };

  const liftSubscriptions = {
    ...(liftSubscriptionData?.subscriptionId
      ? { update: liftSubscriptionData }
      : { add: liftSubscriptionData }
    ),
  };

  return {
    subscription,
    commissionScheme,
    variableBasePriceOverrides,
    buildings,
    liftSubscriptions,
  };
}
